import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { updateNotificationSettings, useApplicationState } from '../utilities/applicationState';
import clientConfig from '../config';
import Toast from './common/toast';
import { get, uniqueId } from 'lodash';

const defaultSessionValue = {
	state: 'LOADING',
	clients: []
};

export const SessionContext = createContext(defaultSessionValue);

export function SessionProvider({ children }) {
	const [initialSession, setInitialSession] = useState(defaultSessionValue);
	const [session, setSession] = useState(defaultSessionValue);
	const [, dispatch] = useApplicationState();
	const [toast, setToast] = useState(false);

	const handleResponse = (message, status = "error") => {
		setToast({
			key: uniqueId(),
			status: status,
			message: message,
			showToast: true
		});
	};

	async function fetchClients() {
		try {
			const response = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/si/clients?page=1&pageSize=10`);
			const data = get(response, 'data.data', []);
			setSession({ ...initialSession, clients: data });
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching Client details.');
			setSession({ ...initialSession, clients: [] });
			handleResponse(errorMessage);
		}
	}

	useEffect(() => {
		if (initialSession.state !== "LOADING" && initialSession.roles.includes('selfservicepartneruser')) {
			fetchClients();
		}
		else {
			setSession({ ...initialSession, clients: [] });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialSession])

	useEffect(() => {

		async function fetchSession() {
			try {
				const result = await axios(`${import.meta.env.VITE_API_PATH}/session`)
				setInitialSession(result.data);
				if(result && result.data && result.data.env) {
					sessionStorage.setItem('ENV', result.data.env);
				}
				if (result.data.roles.includes('selfservicepartneruser') || result.data.roles.includes('selfservicedataclouduser')) {
					fetchNotificationSettings();
				}
				if (result.data.pendoVisitorId && result.data.pendoAccountId) {
					window.pendo.initialize({
						visitor: {
							id: result.data.pendoVisitorId
						},
						account: {
							id: result.data.pendoAccountId
						}
					});
				}
			} catch (e) {
				if (window.location.pathname !== '/access-denied' && window.location.pathname !== '/error' && window.location.pathname !== '/login') {
					const envVar = sessionStorage.getItem('ENV') || import.meta.env.VITE_ENVIRONMENT || 'DEV';
					window.location.href = `${clientConfig.baseUrl[envVar]}/api/authenticate`;
				} else {
					setInitialSession({ roles: [] });
				}
			}
		}

		async function fetchNotificationSettings() {
			try {
				const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/notification-settings`);
				dispatch(updateNotificationSettings(data));
			} catch (e) {
				dispatch(updateNotificationSettings(e.response.data));
			}
		}

		fetchSession();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{toast && <Toast
				key={toast.key}
				status={toast.status}
				message={toast.message}
				showToast={toast.showToast}
			/>}
			<SessionContext.Provider value={session}>{children}</SessionContext.Provider>
		</>)
}

export function withPermissionsAndRoles(Component) {
	return function WrapperComponent(props) {
		return (
			<SessionContext.Consumer>
				{session => <Component {...props} permissions={session.permissions} roles={session.roles} />}
			</SessionContext.Consumer>
		)
	}
}

export function withSession(Component) {
	return function WrapperComponent(props) {
		return (
			<SessionContext.Consumer>
				{session => <Component {...props} session={session} />}
			</SessionContext.Consumer>
		)
	}
}
